import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Share2, 
  TrendingUp, 
  Users, 
  Heart, 
  Eye,
  ExternalLink,
  RefreshCw,
  Calendar,
  BarChart3,
  Download,
  FileText
} from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { SocialConnectionCard } from './SocialConnectionCard';
import { AnalyticsChart } from './AnalyticsChart';
import { ShareableReportModal } from './ShareableReportModal';
import { useAnalyticsData } from '@/hooks/useAnalyticsData';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { isAllowedHost } from '@/utils/security';
import jsPDF from 'jspdf';

// Whitelist of OAuth identity-provider hosts that social-oauth-connect
// is allowed to return. Keep in sync with supabase/functions/social-oauth-connect/index.ts.
const OAUTH_IDP_HOSTS = [
  'api.instagram.com',
  'www.tiktok.com',
  'accounts.google.com',
  'www.paypal.com',
  'www.sandbox.paypal.com',
  'connect.stripe.com',
] as const;

const TikTokIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43V7.83a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.26z"/>
  </svg>
);

interface AnalyticsVaultProps {
  embedded?: boolean;
}

export const AnalyticsVault: React.FC<AnalyticsVaultProps> = ({ embedded = false }) => {
  const { currentUser, accountType } = useUser();
  const { analyticsData, loading, error, refreshData } = useAnalyticsData();
  const { toast } = useToast();
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | null>(null);
  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [reportsRefreshKey, setReportsRefreshKey] = useState(0);

  const generatePdfFromData = useCallback((title: string, description: string, reportPlatforms: string[], reportData: any) => {
    const doc = new jsPDF();
    doc.setFontSize(20);
    doc.text(title, 20, 25);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 33);
    if (description) {
      doc.setFontSize(11);
      doc.setTextColor(60);
      doc.text(description, 20, 42, { maxWidth: 170 });
    }
    doc.setTextColor(0);
    let y = 55;
    doc.setFontSize(14);
    doc.text('Platforms Included', 20, y);
    y += 8;
    doc.setFontSize(11);
    doc.text(reportPlatforms.join(', '), 20, y);
    y += 14;
    doc.setFontSize(14);
    doc.text('Summary Metrics', 20, y);
    y += 10;
    doc.setFontSize(11);
    const stats = reportData?.weeklyStats;
    if (stats) {
      doc.text(`Followers: ${(stats.followers?.current || 0).toLocaleString()} (${stats.followers?.change >= 0 ? '+' : ''}${stats.followers?.change || 0}%)`, 20, y); y += 7;
      doc.text(`Engagement Rate: ${(stats.engagement?.current || 0).toFixed(1)}% (${stats.engagement?.change >= 0 ? '+' : ''}${(stats.engagement?.change || 0).toFixed(1)}%)`, 20, y); y += 7;
      doc.text(`Reach: ${(stats.reach?.current || 0).toLocaleString()} (${stats.reach?.change >= 0 ? '+' : ''}${stats.reach?.change || 0}%)`, 20, y); y += 7;
      doc.text(`Total Likes: ${(stats.likes?.current || 0).toLocaleString()} (${stats.likes?.change >= 0 ? '+' : ''}${stats.likes?.change || 0}%)`, 20, y); y += 7;
    }
    doc.save(`${title.replace(/\s+/g, '_')}.pdf`);
  }, []);

  const RecentReportsList = () => {
    const [reports, setReports] = useState<any[]>([]);
    const [loadingReports, setLoadingReports] = useState(true);

    useEffect(() => {
      const fetchReports = async () => {
        if (!currentUser) return;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', currentUser.id)
          .single();
        if (!profile) { setLoadingReports(false); return; }
        const { data } = await supabase
          .from('analytics_reports' as any)
          .select('*')
          .eq('profile_id', profile.id)
          .order('created_at', { ascending: false })
          .limit(10);
        setReports((data as any[]) || []);
        setLoadingReports(false);
      };
      fetchReports();
    }, [reportsRefreshKey]);

    if (loadingReports) return <p className="text-sm text-muted-foreground">Loading reports...</p>;
    if (reports.length === 0) return <p className="text-sm text-muted-foreground">No reports yet. Generate your first report above.</p>;

    return (
      <div className="space-y-2">
        {reports.map((report: any) => (
          <div key={report.id} className="flex items-center justify-between p-3 border rounded-lg">
            <div className="flex items-center gap-3">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="font-medium text-sm">{report.title}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(report.created_at).toLocaleDateString()} · {(report.platforms || []).join(', ')}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => generatePdfFromData(report.title, report.description || '', report.platforms || [], report.report_data)}
            >
              <Download className="w-4 h-4 mr-1" />
              PDF
            </Button>
          </div>
        ))}
      </div>
    );
  };

  // Only show platforms whose Connect flow is actually wired. Instagram +
  // YouTube were stubbed as `inactive: true` with a "Coming Soon" disabled
  // button — removed to stop showing dead UI. Re-add them here once the
  // OAuth callbacks are implemented.
  const platforms = [
    {
      id: 'tiktok',
      name: 'TikTok',
      icon: TikTokIcon,
      color: 'bg-gradient-to-r from-black to-gray-800',
      connected: analyticsData?.connections?.tiktok || false
    }
  ];

  const weeklyStats = analyticsData?.weeklyStats || {
    followers: { current: 0, change: 0 },
    engagement: { current: 0, change: 0 },
    reach: { current: 0, change: 0 },
    likes: { current: 0, change: 0 }
  };

  const chartData = analyticsData?.chartData || [];

  useEffect(() => {
    // Listen for OAuth completion messages
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'oauth:complete') {
        const { status, platform, error } = event.data;
        
        if (status === 'success') {
          console.log(`Successfully connected to ${platform}`);
          refreshData(); // Refresh to show new connection
        } else {
          console.error(`Failed to connect: ${error}`);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [refreshData]);

  const handleConnect = async (platformId: string) => {
    setConnectingPlatform(platformId);
    
    // Open popup immediately to avoid popup blockers
    const popup = window.open(
      'about:blank',
      'oauth-popup',
      'width=600,height=700,scrollbars=yes'
    );

    if (!popup) {
      toast({
        title: "Popup Blocked",
        description: "Please allow popups for this site and try again.",
        variant: "destructive",
      });
      setConnectingPlatform(null);
      return;
    }

    // Show loading state in popup
    popup.document.write('<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;font-family:system-ui;"><div>Loading...</div></body></html>');

    try {
      console.log(`Starting OAuth flow for ${platformId}...`);
      
      // Make authenticated request to get OAuth URL
      const { data, error } = await supabase.functions.invoke('social-oauth-connect', {
        body: { platform: platformId }
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to get OAuth URL');
      }
      
      if (!data?.authUrl) {
        throw new Error('No OAuth URL returned from server');
      }

      // Validate the returned URL points at a known IdP host before
      // navigating the popup. Guards against a compromised edge
      // function returning an attacker-controlled redirect.
      if (!isAllowedHost(data.authUrl, OAUTH_IDP_HOSTS)) {
        console.error('OAuth URL rejected (host not in allowlist):', data.authUrl);
        throw new Error('Invalid OAuth URL returned from server');
      }

      console.log(`Redirecting to ${platformId} OAuth...`);
      // Navigate to the OAuth URL
      popup.location.href = data.authUrl;

    } catch (error) {
      console.error(`Failed to initiate ${platformId} OAuth:`, error);
      popup.close();
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      toast({
        title: `Failed to connect ${platformId}`,
        description: errorMessage.includes('not configured') 
          ? `Provider credentials not configured. Please contact support.`
          : errorMessage,
        variant: "destructive",
      });
    } finally {
      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = async (platformId: string) => {
    if (!currentUser) return;

    try {
      console.log(`Disconnecting ${platformId}...`);
      
      // Get profile_id
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', currentUser.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Delete connection
      const { error } = await supabase
        .from('social_connections')
        .delete()
        .eq('profile_id', profile.id)
        .eq('platform', platformId);

      if (error) throw error;

      toast({
        title: "Disconnected",
        description: `Successfully disconnected ${platformId}`,
      });

      console.log(`Disconnected from ${platformId}`);
      refreshData(); // Refresh to update UI
    } catch (error) {
      console.error('Failed to disconnect:', error);
      toast({
        title: "Failed to disconnect",
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: "destructive",
      });
    }
  };

  const generateShareableLink = () => {
    setShareModalOpen(true);
  };

  if (!currentUser) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Alert>
          <AlertDescription>
            Please log in to access the Analytics Vault.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className={embedded ? "space-y-8" : "container mx-auto px-4 py-8 space-y-8"}>
      {!embedded && (
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">Analytics Vault</h1>
            <p className="text-muted-foreground">
              Connect your social platforms and share professional analytics with brands
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={refreshData} variant="outline" size="sm" disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh Data
            </Button>
            <Button onClick={generateShareableLink} className="bg-gradient-to-r from-primary to-primary-dark">
              <Share2 className="w-4 h-4 mr-2" />
              Share Report
            </Button>
          </div>
        </div>
      )}
      {embedded && (
        <div className="flex justify-end gap-2">
          <Button onClick={refreshData} variant="outline" size="sm" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh Data
          </Button>
          <Button onClick={generateShareableLink} className="bg-gradient-to-r from-primary to-primary-dark">
            <Share2 className="w-4 h-4 mr-2" />
            Share Report
          </Button>
        </div>
      )}

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="connections" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="connections">Connections</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="reports">Reports</TabsTrigger>
        </TabsList>

        <TabsContent value="connections" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {platforms.map((platform) => (
              <SocialConnectionCard
                key={platform.id}
                platform={platform}
                onConnect={() => handleConnect(platform.id)}
                onDisconnect={() => handleDisconnect(platform.id)}
                isConnecting={connectingPlatform === platform.id}
              />
            ))}
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5" />
                Auto-Pull Schedule
              </CardTitle>
              <CardDescription>
                Stats are automatically updated every week on Sunday at 12:00 AM
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Weekly Data Collection</p>
                  <p className="text-sm text-muted-foreground">
                    Last updated: {analyticsData?.lastUpdated ? new Date(analyticsData.lastUpdated).toLocaleDateString() : 'Never'}
                  </p>
                </div>
                <Badge variant="secondary">Active</Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="analytics" className="space-y-6">
          {!analyticsData?.connections?.instagram && !analyticsData?.connections?.tiktok && !analyticsData?.connections?.youtube ? (
            <Card className="p-8">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-muted rounded-full flex items-center justify-center">
                  <BarChart3 className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold">No profiles linked</h3>
                <p className="text-muted-foreground max-w-md mx-auto">Connect a social platform to see your analytics</p>
              </div>
            </Card>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Followers</p>
                        <p className="text-2xl font-bold">{weeklyStats.followers.current.toLocaleString()}</p>
                      </div>
                      <Users className="w-8 h-8 text-blue-500" />
                    </div>
                    <div className="flex items-center mt-2">
                      <TrendingUp className={`w-4 h-4 mr-1 ${weeklyStats.followers.change >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                      <span className={`text-sm ${weeklyStats.followers.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {weeklyStats.followers.change >= 0 ? '+' : ''}{weeklyStats.followers.change}% this week
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Engagement Rate</p>
                        <p className="text-2xl font-bold">{weeklyStats.engagement.current.toFixed(1)}%</p>
                      </div>
                      <Heart className="w-8 h-8 text-red-500" />
                    </div>
                    <div className="flex items-center mt-2">
                      <TrendingUp className={`w-4 h-4 mr-1 ${weeklyStats.engagement.change >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                      <span className={`text-sm ${weeklyStats.engagement.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {weeklyStats.engagement.change >= 0 ? '+' : ''}{weeklyStats.engagement.change.toFixed(1)}% this week
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Reach</p>
                        <p className="text-2xl font-bold">{weeklyStats.reach.current.toLocaleString()}</p>
                      </div>
                      <Eye className="w-8 h-8 text-purple-500" />
                    </div>
                    <div className="flex items-center mt-2">
                      <TrendingUp className={`w-4 h-4 mr-1 ${weeklyStats.reach.change >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                      <span className={`text-sm ${weeklyStats.reach.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {weeklyStats.reach.change >= 0 ? '+' : ''}{weeklyStats.reach.change}% this week
                      </span>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-muted-foreground">Total Likes</p>
                        <p className="text-2xl font-bold">{weeklyStats.likes.current.toLocaleString()}</p>
                      </div>
                      <Heart className="w-8 h-8 text-pink-500" />
                    </div>
                    <div className="flex items-center mt-2">
                      <TrendingUp className={`w-4 h-4 mr-1 ${weeklyStats.likes.change >= 0 ? 'text-green-500' : 'text-red-500'}`} />
                      <span className={`text-sm ${weeklyStats.likes.change >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {weeklyStats.likes.change >= 0 ? '+' : ''}{weeklyStats.likes.change}% this week
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Growth Analytics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AnalyticsChart data={chartData} />
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        <TabsContent value="reports" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Shareable Reports</CardTitle>
              <CardDescription>
                Generate professional reports to share with brands and sponsors
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button onClick={generateShareableLink} className="w-full">
                <Share2 className="w-4 h-4 mr-2" />
                Generate New Report
              </Button>
              
              <div className="space-y-2">
                <h4 className="font-medium">Recent Reports</h4>
                <RecentReportsList />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <ShareableReportModal 
        open={shareModalOpen} 
        onOpenChange={(open) => {
          setShareModalOpen(open);
          if (!open) {
            // Trigger re-render of reports list
            setReportsRefreshKey(prev => prev + 1);
          }
        }}
        analyticsData={analyticsData}
        platforms={platforms}
      />
    </div>
  );
};